export interface ApiClientConfig {
    /** Dynamic base URL getter - called for each request to support region switching */
    getBaseUrl: () => string;
    getAccessToken: () => Promise<string>;
    getTenantId?: () => string | null;
}

export interface ApiErrorBody {
    error: string;
    message: string;
    details?: unknown;
}

export class ApiError extends Error {
    constructor(
        public status: number,
        public statusText: string,
        public body: ApiErrorBody | null
    ) {
        super(body?.message ?? `API Error: ${status} ${statusText}`);
        this.name = 'ApiError';
    }

    get errorCode(): string | undefined {
        return this.body?.error;
    }
}

type ResponseKind = 'json' | 'arrayBuffer';

interface RequestOptions {
    /**
     * Plain object → JSON-stringified with `application/json` Content-Type.
     * FormData → passed through (browser sets the multipart boundary).
     * ArrayBuffer / typed array → passed through with
     * `application/octet-stream` Content-Type.
     */
    body?: unknown;
    headers?: Record<string, string>;
    skipAuth?: boolean;
    /** Override base URL for this request (bypasses region context). */
    baseUrl?: string;
    signal?: AbortSignal;
    response?: ResponseKind;
}

export class ApiClient {
    constructor(private config: ApiClientConfig) {}

    private async buildHeaders(options?: RequestOptions): Promise<Record<string, string>> {
        const headers: Record<string, string> = { ...options?.headers };
        if (!options?.skipAuth) {
            const token = await this.config.getAccessToken();
            headers['Authorization'] = `Bearer ${token}`;
        }
        const tenantId = this.config.getTenantId?.();
        if (tenantId) {
            headers['X-Tenant-Id'] = tenantId;
        }
        return headers;
    }

    private encodeBody(body: unknown, headers: Record<string, string>): BodyInit | undefined {
        if (body === undefined || body === null) return undefined;
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
            // Let the browser fill in the multipart boundary.
            return body;
        }
        if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
            if (!('Content-Type' in headers)) {
                headers['Content-Type'] = 'application/octet-stream';
            }
            return body as BodyInit;
        }
        if (!('Content-Type' in headers)) {
            headers['Content-Type'] = 'application/json';
        }
        return JSON.stringify(body);
    }

    private async request<T>(method: string, path: string, options?: RequestOptions): Promise<T> {
        const headers = await this.buildHeaders(options);
        const body = this.encodeBody(options?.body, headers);
        const baseUrl = options?.baseUrl ?? this.config.getBaseUrl();
        const response = await fetch(`${baseUrl}${path}`, {
            method,
            headers,
            body,
            signal: options?.signal,
        });

        if (!response.ok) {
            const errBody = await response.json().catch(() => null);
            throw new ApiError(response.status, response.statusText, errBody);
        }

        if (response.status === 204) {
            return undefined as T;
        }

        if (options?.response === 'arrayBuffer') {
            return (await response.arrayBuffer()) as unknown as T;
        }

        return response.json();
    }

    get<T>(
        path: string,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<T> {
        return this.request<T>('GET', path, options);
    }

    post<T>(
        path: string,
        body?: unknown,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<T> {
        return this.request<T>('POST', path, { body, ...options });
    }

    patch<T>(
        path: string,
        body: unknown,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<T> {
        return this.request<T>('PATCH', path, { body, ...options });
    }

    delete<T>(
        path: string,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<T> {
        return this.request<T>('DELETE', path, options);
    }

    put<T>(
        path: string,
        body?: unknown,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<T> {
        return this.request<T>('PUT', path, { body, ...options });
    }

    /**
     * Download binary data (ArrayBuffer) via GET request.
     * Used for downloading files and volume data.
     */
    getBinary(
        path: string,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<ArrayBuffer> {
        return this.request<ArrayBuffer>('GET', path, { ...options, response: 'arrayBuffer' });
    }

    /**
     * Upload binary data (ArrayBuffer) via PUT request.
     * Used for chunked file uploads where raw binary data is sent.
     */
    putBinary<T>(
        path: string,
        data: ArrayBuffer,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<T> {
        return this.request<T>('PUT', path, { body: data, ...options });
    }

    /**
     * Send a multipart/form-data POST. The browser sets the Content-Type
     * header (including the boundary) when given a FormData body, so we
     * don't supply it explicitly.
     */
    postMultipart<T>(
        path: string,
        formData: FormData,
        options?: { skipAuth?: boolean; baseUrl?: string; signal?: AbortSignal }
    ): Promise<T> {
        return this.request<T>('POST', path, { body: formData, ...options });
    }
}
